import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const materialsErrorTypeValues = [
  "MATERIALS_ORDER_NOT_FOUND",
  "MATERIALS_SYNC_INVALID_PROJECT",
  "MATERIALS_SYNC_BLOCKED",
  "MATERIALS_ORDER_ALREADY_SYNCED_TO_BILL",
  "MATERIALS_ORDER_CANCEL_BLOCKED",
  "VENDOR_CREDIT_AMOUNT_MISMATCH",
] as const;
const materialsErrorTypeSchema = z.enum(materialsErrorTypeValues);
const MaterialsErrorType = materialsErrorTypeSchema.enum;
type MaterialsErrorType = z.infer<typeof materialsErrorTypeSchema>;

export type MaterialsErrors =
  | {
      errorType: typeof MaterialsErrorType.MATERIALS_ORDER_NOT_FOUND;
      data: { materialsOrderId: string };
    }
  | {
      errorType: typeof MaterialsErrorType.MATERIALS_SYNC_INVALID_PROJECT;
      data: { projectId: number; reason: string };
    }
  | {
      errorType: typeof MaterialsErrorType.MATERIALS_SYNC_BLOCKED;
      data: { projectId: number; reason: string };
    }
  | {
      errorType: typeof MaterialsErrorType.MATERIALS_ORDER_ALREADY_SYNCED_TO_BILL;
      data: { projectId: number; existingBillId: string; materialsOrderExternalId: string };
    }
  | {
      errorType: typeof MaterialsErrorType.MATERIALS_ORDER_CANCEL_BLOCKED;
      data: { externalId: string; vendor: string; projectIds: number[]; reason: string };
    }
  | {
      errorType: typeof MaterialsErrorType.VENDOR_CREDIT_AMOUNT_MISMATCH;
      data: { existingVendorCreditId: string; existingAmount: number; newAmount: number };
    };

export const materialsErrorDetails: Record<MaterialsErrorType, ErrorDetails> = {
  [MaterialsErrorType.MATERIALS_ORDER_NOT_FOUND]: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Materials order not found",
  },
  [MaterialsErrorType.MATERIALS_SYNC_INVALID_PROJECT]: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Materials sync failed - invalid project",
  },
  [MaterialsErrorType.MATERIALS_SYNC_BLOCKED]: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Materials sync blocked - verify contractor costs task is completed",
  },
  [MaterialsErrorType.MATERIALS_ORDER_ALREADY_SYNCED_TO_BILL]: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Materials order already synced to another bill",
  },
  [MaterialsErrorType.MATERIALS_ORDER_CANCEL_BLOCKED]: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Vendor cancelled a materials order whose bill is locked — resolve manually",
  },
  [MaterialsErrorType.VENDOR_CREDIT_AMOUNT_MISMATCH]: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Vendor credit amount differs from quickbooks — updating to match",
  },
};
